import { v4 as uuidv4 } from "uuid";

import { fetchWithAuth } from "~/common/fetch";
import { API_DOCTOR_PAYMENTS, API_DOCTORS_TASKS, API_LOGOUT, API_PATIENT_IMAGE, API_PATIENTS_LIST } from "~/config";
import type { TUserActionNotification } from "~/reducers/dashboard";
import type { AppDispatch } from "~/store";

import { addUserActionNotification } from "./user_notification";
import { convertBytesToMB } from "~/common/utils";

function userLogoutFailure(message: string) {
  return { type: "USER_LOGOUT_ERROR", message };
}

function clearState() {
  return { type: "CLEAR_STATE" };
}

export function removeMediaItem(id: string) {
  return { type: "REMOVE_MEDIA_ITEM" as const, id };
}

export function handleDropped(
  img: File,
  patient_id: number,
  semantics: string,
  id: string,
  correctionNum: number | undefined,
  clbk: () => void,
  maxFileSize?: number,
  onProgress?: (percent: number) => void,
  onError?: () => void,
  temporaryUpload?: { id: string; isActive(): boolean },
) {
  return (dispatch: AppDispatch) => {
    const maxFileSizeMb = convertBytesToMB(maxFileSize);
    const MSG_NOTIFICATION_ERROR: TUserActionNotification = {
      message: "notify.upload.E_PATIENT_FILE_TOO_BIG",
      values: { maxFileSizeMb },
      level: "error",
      position: "tr",
      autoDismiss: 4,
    };

    if (maxFileSize && img.size > maxFileSize) {
      dispatch(addUserActionNotification(MSG_NOTIFICATION_ERROR));
      onError?.();
      return;
    }

    const url = API_PATIENT_IMAGE(patient_id, semantics);
    const image = {
      img: img,
      semantics: semantics,
      id: id,
      patient_id: patient_id,
    };
    const fn = img.name.toLowerCase();
    if (fn.endsWith(".jpg") || fn.endsWith(".jpeg") || fn.endsWith(".png")) {
      dispatch({ type: "PATIENT_IMAGE_PREVIEW", image });
    }

    const data = new FormData();
    data.append("file", img);
    data.append("upload_id", temporaryUpload?.id ?? uuidv4());

    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      const percent = (e.loaded / e.total) * 100;
      $(`#__PROGRESSBAR__-${id}`).css({ width: `${percent}%` });
      onProgress?.(percent);
    };
    xhr.onload = (e) => {
      let json;
      try {
        json = JSON.parse(e.target.responseText);
      } catch {
        $(`#__PROGRESSBAR__-${id}`).hide();
        onError?.();
        return;
      }
      if (json.status === "ok") {
        // если передается correctionNum (номер коррекции)
        // тогда имя фотографии меняется на correction_номер_семантика
        // поле semantics меняется на recipe
        if (temporaryUpload && !temporaryUpload.isActive()) {
          $(`#__PROGRESSBAR__-${id}`).hide();
          clbk?.();
          return;
        }
        if (correctionNum) {
          const file_extension = json.user_filename.split(".").slice(-1)[0];
          const new_filename = "correction_" + correctionNum + "_" + json.semantics + "." + file_extension;
          dispatch({
            type: "PATIENT_IMAGE_UPLOAD_SUCCESS",
            json: Object.assign({}, json, { id }, { semantics: "recipe" }, { user_filename: new_filename }),
          });
        } else {
          dispatch({
            type: "PATIENT_IMAGE_UPLOAD_SUCCESS",
            json: Object.assign({}, json, { id }),
          });
        }
        !clbk || clbk();
        $(`#__PROGRESSBAR__-${id}`).hide();
        // $(`#__PROGRESSBAR__-${ id }`).css({ width: '0%' });
      } else {
        // JFIF uploads can have an allowed MIME type but an extension rejected by the backend.
        if (json.message == "E_UNSUPPORTED_FORMAT") {
          dispatch(
            addUserActionNotification({
              message: "notify.upload.E_UNSUPPORTED_FORMAT",
              level: "error",
            }),
          );
        }
        //dispatch({type: 'PATIENT_IMAGE_UPLOAD_ERROR', json: Object.assign({}, json, {id})});
        $(`#__PROGRESSBAR__-${id}`).hide();
        onError?.();
      }
    };
    xhr.onerror = (e) => {
      //dispatch({type: 'PATIENT_IMAGE_UPLOAD_ERROR', json: Object.assign({}, {id}, e.target.type)});
      $(`#__PROGRESSBAR__-${id}`).hide();
      onError?.();
    };
    $(`#__PROGRESSBAR__-${id}`).show();
    xhr.send(data);

    // .catch((err) => dispatch({type: 'PATIENT_IMAGE_UPLOAD_ERROR', json: Object.assign({}, {id: id}, err)}))
  };
}

export function getPatientsData(status?: string | false, ordering?: "patient_id" | "-patient_id" | "patient_name" | "-patient_name", search?: string | false, page?: number, perPage?: number) {
  const params = new URLSearchParams();

  if (ordering) params.append("ordering", ordering);
  if (page) params.append("page", String(page));
  if (perPage) params.append("perPage", String(perPage));
  if (status) params.append("status", status);
  if (search) params.append("search", search);

  const url = `${API_PATIENTS_LIST}?${params.toString()}`;

  return async (dispatch: AppDispatch) => {
    dispatch(loadSpinner());
    try {
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        console.error(`Failed on ${API_PATIENTS_LIST}`);
        throw Error(response.statusText);
      }
      const json = await response.json();
      dispatch({ type: "GET_PATIENTS_SUCCESS", json });
    } catch (err) {
      dispatch({ type: "GET_PATIENTS_ERROR", err });
    } finally {
      dispatch(clearSpinner());
    }
  };
}

/** Read a filtered patient count without replacing the visible patient-list state. */
export async function fetchPatientsCount(status: string | false): Promise<number | null> {
  const params = new URLSearchParams();
  // The backend requires `ordering` and `page` even for a count-only request.
  params.append("ordering", "-patient_id");
  params.append("page", "1");
  params.append("perPage", "1");
  if (status) params.append("status", status);
  try {
    const response = await fetchWithAuth(`${API_PATIENTS_LIST}?${params.toString()}`);
    if (!response.ok) {
      console.error(`fetchPatientsCount: ${API_PATIENTS_LIST}?${params.toString()} → ${response.status}`);
      return null;
    }
    const json: unknown = await response.json();
    const total = (json as { total?: unknown })?.total;
    // This endpoint may return `total` as either a number or a numeric string.
    const parsed = typeof total === "number" ? total : typeof total === "string" ? Number(total) : NaN;
    if (!Number.isFinite(parsed)) {
      console.error(`fetchPatientsCount: unexpected "total" in response`, json);
      return null;
    }
    return parsed;
  } catch (err) {
    console.error(`fetchPatientsCount failed`, err);
    return null;
  }
}

export function getDoctorsTasks() {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_DOCTORS_TASKS);
      if (!response.ok) {
        console.error(`Failed on ${API_DOCTORS_TASKS}`);
        throw Error(response.statusText);
      }
      const json = await response.json();
      return dispatch({ type: "GET_DOCTORS_TASKS_SUCCESS", json });
    } catch (err) {
      return dispatch({ type: "GET_DOCTORS_TASKS_ERROR", err });
    }
  };
}

export function getDoctorsPays() {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_DOCTOR_PAYMENTS);
      if (!response.ok) {
        console.error(`Failed on ${API_DOCTOR_PAYMENTS}`);
        throw Error(response.statusText);
      }
      const json = await response.json();
      return dispatch({ type: "GET_DOCTORS_PAYS_SUCCESS", json });
    } catch (err) {
      return dispatch({ type: "GET_DOCTORS_PAYS_ERROR", err });
    }
  };
}

export function logoutUser() {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await fetchWithAuth(API_LOGOUT, { method: "POST" });
      if (!response.ok) {
        console.error(`Failed logging out on ${API_LOGOUT}`);
        throw Error(response.statusText);
      }
      dispatch(clearState());
    } catch (err) {
      dispatch(userLogoutFailure("logout failure: " + err));
    }
  };
}

export function eraseStateProp(prop_name: "patient" | "media" | "comments" | "instructions" | "doctor") {
  return { type: "ERASE_" + prop_name.toUpperCase() };
}

export function setCbctFiles(files: string[]) {
  return { type: "CBCT_UPLOAD_SUCCESS" as const, payload: files };
}

export function cbctUploadSuccess(files) {
  return (dispatch) => {
    files = files.map((file) => file.name);
    dispatch({ type: "CBCT_UPLOAD_SUCCESS", payload: files });
  };
}

function loadSpinner() {
  return { type: "LOAD_SPINNER" };
}

function clearSpinner() {
  return { type: "CLEAR_SPINNER" };
}
